import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast, Bounce } from 'react-toastify';
import { createUniformSizeApi, updateUniformSizeApi } from 'api/services/uniformManagement';
import FormModalShell, { StatusToggleRow } from 'components/modelpopup/FormModalShell';

const schema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  code: Yup.string().required('Code is required'),
  status: Yup.boolean()
});

const Field = ({ label, required, error, children }) => (
  <div className="mb-3">
    <label
      style={{
        display: 'block',
        fontSize: 12.5,
        fontWeight: 600,
        color: '#374151',
        marginBottom: 6,
        letterSpacing: '0.01em',
      }}
    >
      {label}
      {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
    </label>
    {children}
    {error && (
      <small style={{ color: '#dc2626', fontSize: 12, marginTop: 4, display: 'block' }}>
        {error}
      </small>
    )}
  </div>
);

const SizeFormModal = ({ open, onClose, selectedSize, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, formState: { errors }, setValue, reset, setError } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: '', code: '', status: true }
  });

  useEffect(() => {
    if (open) {
      if (selectedSize) {
        setValue('name', selectedSize.name);
        setValue('code', selectedSize.code);
        setValue('status', selectedSize.status);
      } else {
        reset({ name: '', code: '', status: true });
      }
    }
  }, [selectedSize, open]);

  const onSubmit = (data) => {
    setLoading(true);
    const apiCall = selectedSize
      ? updateUniformSizeApi(selectedSize.id, data)
      : createUniformSizeApi(data);

    apiCall.then(() => {
      toast.success(selectedSize ? 'Size updated!' : 'Size created!', {
        position: "top-right", autoClose: 3000, transition: Bounce
      });
      setLoading(false);
      reset();
      onSuccess();
    }).catch(e => {
      setLoading(false);
      try {
        if (e.response?.data) {
          Object.entries(e.response.data).forEach(([k, v]) => {
            setError(k, { type: 'manual', message: Array.isArray(v) ? v.join(", ") : v });
          });
        }
      } catch {
        toast.error('Operation failed', { position: "top-right", autoClose: 3000, transition: Bounce });
      }
    });
  };

  return (
    <FormModalShell
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      title={selectedSize ? "Edit Size" : "Add Size"}
      subtitle="Name and short code displayed across uniform forms."
      submitLabel={selectedSize ? "Update" : "Create"}
      loading={loading}
      width={520}
    >
      <div className="row g-3">
        <div className="col-md-6">
          <Field label="Name" required error={errors.name?.message}>
            <Controller name="name" control={control}
              render={({ field }) => (
                <input className="form-control" {...field} placeholder="e.g. Small" />
              )} />
          </Field>
        </div>
        <div className="col-md-6">
          <Field label="Code" required error={errors.code?.message}>
            <Controller name="code" control={control}
              render={({ field }) => (
                <input className="form-control" {...field} placeholder="e.g. S" />
              )} />
          </Field>
        </div>
        <div className="col-12">
          <Controller name="status" control={control}
            render={({ field: { value, onChange } }) => (
              <StatusToggleRow
                value={value}
                onChange={onChange}
                helper="Inactive sizes are hidden from new issuance forms."
              />
            )} />
        </div>
      </div>
    </FormModalShell>
  );
};

export default SizeFormModal;
